// Thumbs rating on an assistant turn.
//
// The ONLY direct quality measurement this product has. Everything else on the
// dashboard is a proxy — citation counts, refusal rates, how often a draft gets
// revised — and proxies can all look healthy while the answers are wrong. That is
// this app's defining failure mode, so the one control that measures it directly
// earns its place in the pane.
//
// Deliberately understated: no prompt, no "how did I do?", no modal. An HR person
// mid-task should be able to ignore it completely, and a rating widget that nags
// gets rated to make it go away, which is worse than no data.

import { useState } from "react";
import { sendFeedback } from "../api/client";

export type Rating = "up" | "down" | null;

export function Feedback({
  usageEventId,
  value,
  onChange,
}: {
  usageEventId: number;
  value: Rating;
  onChange: (next: Rating) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function rate(next: Rating) {
    if (busy) return;
    // Clicking the active thumb clears it, so a mis-click is undoable without
    // hunting for an undo affordance.
    const resolved: Rating = value === next ? null : next;
    setBusy(true);
    // Optimistic: the thumb flips immediately and stays flipped even if the write
    // fails. A rating is not worth an error state in front of the user — the
    // failure is logged in sendFeedback, which is where someone debugging will look.
    onChange(resolved);
    await sendFeedback(usageEventId, resolved);
    setBusy(false);
  }

  return (
    <div className="tp-feedback">
      <span className="tp-feedback-label">Was this helpful?</span>
      <button
        type="button"
        className={`tp-feedback-btn${value === "up" ? " is-on" : ""}`}
        aria-pressed={value === "up"}
        aria-label="Helpful"
        title="Helpful"
        onClick={() => void rate("up")}
      >
        <ThumbIcon />
      </button>
      <button
        type="button"
        className={`tp-feedback-btn is-down${value === "down" ? " is-on" : ""}`}
        aria-pressed={value === "down"}
        aria-label="Not helpful"
        title="Not helpful"
        onClick={() => void rate("down")}
      >
        <ThumbIcon />
      </button>
    </div>
  );
}

/** One glyph, flipped with CSS for the down state — half the markup, and the two
 *  can never drift apart visually. */
function ThumbIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M6.6 14h4.9c.7 0 1.3-.4 1.5-1.1l1.4-4.1c.05-.15.08-.3.08-.46V7.2c0-.66-.54-1.2-1.2-1.2H9.9l.5-2.5c.05-.27 0-.55-.17-.77l-.6-.73L5.9 6.1c-.2.24-.3.54-.3.85V13c0 .55.45 1 1 1zM2.8 6h1.4c.33 0 .6.27.6.6v6.8c0 .33-.27.6-.6.6H2.8a.6.6 0 0 1-.6-.6V6.6c0-.33.27-.6.6-.6z"
      />
    </svg>
  );
}
